import {FC} from 'react'
import {PublicExpertiseService} from '../../../services/expertiseSearchService'
import {sanitizeHtml} from '../../../helpers/sanitizeHtml'

/**
 * Tarjeta de un servicio ofrecido (Figma 4457:76983 y su estado seleccionado 4458:89557).
 *
 * Una sola definicion para las tres pantallas del flujo. En el booking la tarjeta ES el control de
 * seleccion, asi que se renderiza como <button>; con `variant='static'` deja de serlo, para la
 * pestana Expertise del perfil, donde solo informa.
 */
type Props = {
  service: PublicExpertiseService
  variant?: 'selectable' | 'static'
  selected?: boolean
  onSelect?: () => void
  /** Muestra "by <experto>": util en el buscador, donde la tarjeta viaja fuera de su ficha. */
  showAuthor?: boolean
}

/** La tarifa, o el texto que la sustituye cuando el servicio se negocia caso por caso. */
export const rateLabel = (service: PublicExpertiseService) => {
  if (service.rate === null || service.rate === undefined) return 'Under Negotiation'
  const n = Number(service.rate)
  if (isNaN(n)) return String(service.rate)
  const body =
    n % 1 === 0 ? n.toLocaleString('en-US') : n.toLocaleString('en-US', {minimumFractionDigits: 2})
  return `$${body}`
}

export const ExpertiseOfferCard: FC<Props> = ({
  service,
  variant = 'selectable',
  selected,
  onSelect,
  showAuthor,
}) => {
  const isStatic = variant === 'static'
  const className = [
    'expertise-offer-card',
    isStatic ? 'expertise-offer-card--static' : '',
    selected ? 'expertise-offer-card--selected' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const body = (
    <>
      <span className='expertise-offer-card__title'>{service.title}</span>

      {showAuthor && service.partner && (
        <span className='expertise-offer-card__by'>by {service.partner.companyName}</span>
      )}

      {/* La descripcion viene del editor rich-text (Quill) del formulario de Expertise;
          renderizarla como texto plano mostraba las etiquetas <p><strong> literales al comprador.
          Pasa por sanitizeHtml (misma politica que Biography/Community) para bloquear script/iframe/
          eventos inline; ALLOWED_TAGS ya cubre p/strong/em/ul/li/etc. */}
      <div
        className='expertise-offer-card__text expertise-offer-card__text--rich'
        dangerouslySetInnerHTML={{__html: sanitizeHtml(service.description)}}
      />

      <div className='expertise-offer-card__rate'>
        <div className='expertise-offer-card__rate-body'>
          <span className='expertise-offer-card__rate-label'>Rate</span>
          <span className='expertise-offer-card__rate-value'>{rateLabel(service)}</span>
        </div>
        {service.rateUnit && (
          <span className='expertise-offer-card__rate-unit'>/ {service.rateUnit}</span>
        )}
      </div>
    </>
  )

  if (isStatic) return <div className={className}>{body}</div>

  return (
    <button type='button' className={className} onClick={onSelect} aria-pressed={Boolean(selected)}>
      {body}
    </button>
  )
}

export default ExpertiseOfferCard
